import type { Config } from '@/config/types.config';
import { DtoOrderOrderStatusEnum, DtoOrderOrderTypeEnum } from '@repo/api-types/order-retrieval';
import { assetTypes } from '@repo/types/asset';

import { createApiConfig } from '@/api/helpers.api';

const API_V1_BASE_PATH = 'https://salja.ingka.com';
const API_V2_BASE_PATH = 'https://api.saljaot.selling.ingka.com';
const FUNCTION_ENDPOINT_BASE_PATH = 'https://bff.saljaot.selling.ingka.com/';
const ACCEPTED_ATTACHMENT_FILE_TYPES = '.pdf,.gif,.jpg,.xls,.jfif,.pptx,.tiff,.jpeg,.xlsx,.docx,.png,.doc';

const config: Config = {
    api: {
        ...createApiConfig({
            classicSaljaBaseUrl: API_V1_BASE_PATH,
            orderTakingBaseUrl: API_V2_BASE_PATH,
            orderTakingBffBaseUrl: FUNCTION_ENDPOINT_BASE_PATH,
        }),
    },
    cartDomain: 'https://coworker.ingka.com',
    cartPath: 'v1/cart',
    cartHomePath: 'v1',
    addressFormatStandard: '{{addressLine1}}, {{zipCode}}, {{city}}',
    addressFormatPuP: '{{addressLine1}} {{addressLine4}}, {{zipCode}}, {{city}}',
    dateTimeFormat: 'DD.MM.YYYY, HH:mm',
    dateFormat: 'DD.MM.YYYY',
    timeFormat: 'HH:mm',
    priceFormat: {
        useCurrencySymbolMap: true,
    },
    measurementSystem: 'metric',
    feature: {
        fragments: {
            orderHistory: true,
            manualPayments: false,
        },
        articleDeliveryView: {
            enable: true,
        },
        articleServiceView: {
            enable: true,
        },
        extraNeedRequest: {
            enable: false,
        },
        paymentsRims: {
            enable: false,
        },
        orderHistory: {
            enable: true,
        },
        priceExclTax: {
            enable: false,
        },
        paymentLinkForRegisteredCustomersOnly: {
            enable: false,
        },
        kitchenLink: {
            enable: false,
        },
        messages: {
            TSP_CUST: {
                add: {
                    enable: false,
                },
                edit: {
                    enable: false,
                },
                read: {
                    enable: false,
                },
            },
            EXTERNAL: {
                add: {
                    enable: false,
                },
                edit: {
                    enable: false,
                },
                read: {
                    enable: false,
                },
            },
            INTERNAL: {
                add: {
                    enable: false,
                },
                edit: {
                    enable: false,
                },
                read: {
                    enable: false,
                },
            },
        },
        draftV2Service: {
            enable: true,
        },
        docusign: {
            enable: false,
            acceptedFileTypes: ['.pdf'],
        },
        redirectToCartOnAssetShare: {
            enable: true,
        },
        showPriceSummary: {
            enable: true,
        },
        pickingListPdf: {
            enable: false,
            enabledFor: [
                {
                    assetType: assetTypes.draft,
                },
            ],
        },
        attachments: {
            add: {
                enableFor: [assetTypes.order],
                acceptedFileTypes: ACCEPTED_ATTACHMENT_FILE_TYPES,
            },
            edit: {
                enableFor: [assetTypes.order],
            },
            remove: {
                enableFor: [assetTypes.order],
            },
            editComments: {
                enableFor: [assetTypes.order],
            },
            maxSizeMb: 7,
        },
        services: {
            timeWindowSelection: {
                delivery: {
                    enable: false,
                    enabledFor: [
                        {
                            assetType: assetTypes.order,
                            orderTypes: [DtoOrderOrderTypeEnum.ISOM],
                            statuses: [
                                DtoOrderOrderStatusEnum.CREATED,
                                DtoOrderOrderStatusEnum.COMPLETED,
                                DtoOrderOrderStatusEnum.IN_PROGRESS,
                            ],
                        },
                    ],
                    permissions: ['running-order.can-perform-standard-changes'],
                },
                providedService: {
                    enable: false,
                    enabledFor: [
                        {
                            assetType: assetTypes.order,
                            orderTypes: [DtoOrderOrderTypeEnum.ISOM],
                            statuses: [
                                DtoOrderOrderStatusEnum.CREATED,
                                DtoOrderOrderStatusEnum.COMPLETED,
                                DtoOrderOrderStatusEnum.IN_PROGRESS,
                            ],
                        },
                    ],
                    permissions: ['running-order.can-perform-standard-changes'],
                },
                disabledServiceIds: ['SGR20001032'],
            },
        },
        commandMenu: {
            enable: false,
        },
        questionnaire: {
            read: {
                enable: true,
                enabledFor: [
                    {
                        assetType: assetTypes.order,
                        orderTypes: [DtoOrderOrderTypeEnum.ISOM],
                        statuses: [
                            DtoOrderOrderStatusEnum.CREATED,
                            DtoOrderOrderStatusEnum.COMPLETED,
                            DtoOrderOrderStatusEnum.IN_PROGRESS,
                        ],
                    },
                ],
            },
            write: {
                enable: false,
                enabledFor: [
                    {
                        assetType: assetTypes.order,
                        orderTypes: [DtoOrderOrderTypeEnum.ISOM],
                        statuses: [
                            DtoOrderOrderStatusEnum.CREATED,
                            DtoOrderOrderStatusEnum.COMPLETED,
                            DtoOrderOrderStatusEnum.IN_PROGRESS,
                        ],
                    },
                ],
            },
        },
        paymentDateModification: {
            read: {
                enable: false,
                enabledFor: [
                    {
                        assetType: assetTypes.order,
                        orderTypes: [DtoOrderOrderTypeEnum.ISOM],
                    },
                ],
            },
            edit: {
                enable: false,
                enabledFor: [
                    {
                        assetType: assetTypes.order,
                        orderTypes: [DtoOrderOrderTypeEnum.ISOM],
                    },
                ],
                permissions: ['unpaid-order.can-change-advance-pay-due-date'],
            },
        },
    },
    shareOptions: {
        smsEnabledFor: {
            assetTypes: [assetTypes.order, assetTypes.draft],
            orderTypes: [DtoOrderOrderTypeEnum.ISOM, DtoOrderOrderTypeEnum.SHLIST],
        },
        emailEnabledFor: [assetTypes.order, assetTypes.quote, assetTypes.draft],
        printEnabledFor: [assetTypes.order, assetTypes.quote, assetTypes.draft],
    },
    filters: {
        noOfDays: {
            enable: true,
            default: '30',
            options: ['7', '30', '90'],
        },
    },
} as const;

export default config;
